import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserRole, isDevAdminEnabled, isFirebaseConfigured } from './config';
import {
    SignedInUser,
    signInAdminWithGoogle,
    signInOwnerWithGoogle,
    signInOwnerWithPassword,
    signOutFirebase,
    subscribeToAdminAuth,
} from './firebase';

export type { UserRole };

export type AuthUser = {
    email: string;
    displayName: string | null;
    role: UserRole;
};

type AuthContextValue = {
    user: AuthUser | null;
    loading: boolean;
    firebaseReady: boolean;
    devAdminEnabled: boolean;
    signInWithGoogle: () => Promise<void>;
    signInOwnerGoogle: () => Promise<void>;
    signInOwnerEmail: (email: string, password: string) => Promise<void>;
    continueAsDevAdmin: () => void;
    signOut: () => Promise<void>;
};

const DEV_SESSION_KEY = 'galia-dev-admin';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const toAuthUser = (user: SignedInUser): AuthUser => ({
    email: user.email,
    displayName: user.displayName,
    role: user.role,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);
    const localAdminRef = useRef(false);
    const firebaseReady = isFirebaseConfigured();
    const devAdminEnabled = isDevAdminEnabled();

    useEffect(() => {
        let cancelled = false;
        let unsubscribe: () => void = () => {};

        if (devAdminEnabled && sessionStorage.getItem(DEV_SESSION_KEY) === '1') {
            localAdminRef.current = true;
            setUser({
                email: 'dev-admin@local',
                displayName: 'Local admin',
                role: 'admin',
            });
            setLoading(false);
            return;
        }

        void subscribeToAdminAuth((nextUser) => {
            if (cancelled || localAdminRef.current) {
                return;
            }
            setUser(nextUser ? toAuthUser(nextUser) : null);
            setLoading(false);
        }).then((stop) => {
            if (cancelled) {
                stop();
                return;
            }
            unsubscribe = stop;
        }).catch((error) => {
            console.error('Firebase auth subscribe failed', error);
            if (!cancelled) {
                setUser(null);
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [devAdminEnabled]);

    const signInWithGoogle = useCallback(async () => {
        const nextUser = await signInAdminWithGoogle();
        setUser(toAuthUser(nextUser));
    }, []);

    const signInOwnerGoogle = useCallback(async () => {
        const nextUser = await signInOwnerWithGoogle();
        setUser(toAuthUser(nextUser));
    }, []);

    const signInOwnerEmail = useCallback(async (email: string, password: string) => {
        const nextUser = await signInOwnerWithPassword(email, password);
        setUser(toAuthUser(nextUser));
    }, []);

    const continueAsDevAdmin = useCallback(() => {
        if (!isDevAdminEnabled()) {
            return;
        }

        localAdminRef.current = true;
        sessionStorage.setItem(DEV_SESSION_KEY, '1');
        setUser({
            email: 'dev-admin@local',
            displayName: 'Local admin',
            role: 'admin',
        });
        setLoading(false);
    }, []);

    const signOut = useCallback(async () => {
        localAdminRef.current = false;
        sessionStorage.removeItem(DEV_SESSION_KEY);
        try {
            await signOutFirebase();
        } catch (error) {
            console.error('Firebase sign-out failed', error);
        }
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({
            user,
            loading,
            firebaseReady,
            devAdminEnabled,
            signInWithGoogle,
            signInOwnerGoogle,
            signInOwnerEmail,
            continueAsDevAdmin,
            signOut,
        }),
        [
            user,
            loading,
            firebaseReady,
            devAdminEnabled,
            signInWithGoogle,
            signInOwnerGoogle,
            signInOwnerEmail,
            continueAsDevAdmin,
            signOut,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
